<template>
  <div class="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 flex items-center justify-center p-4 sm:p-6">
    <div class="w-full max-w-md">
      <!-- Logo -->
      <div class="text-center mb-6 sm:mb-8">
        <div class="flex justify-center mb-4">
          <img src="/logo.png" alt="Logo" class="h-16 sm:h-20 w-auto object-contain" />
        </div>
      </div>

      <div class="bg-white border border-gray-200 rounded-2xl shadow-xl p-6 sm:p-8">
        <div class="flex justify-center mb-4">
          <div class="p-3 bg-[#22282F]/10 rounded-xl">
            <ShieldCheck class="w-7 h-7 text-[#22282F]" />
          </div>
        </div>

        <h2 class="text-xl sm:text-2xl font-bold text-gray-900 mb-1 text-center">
          Vérification en deux étapes
        </h2>
        <p class="text-gray-500 text-sm text-center mb-6">
          Saisissez le code à 6 chiffres généré par votre application
          d'authentification (Google Authenticator, Authy…).
        </p>

        <!-- Erreur -->
        <div
          v-if="error"
          class="mb-5 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2"
        >
          <AlertCircle class="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p class="text-sm text-red-800">{{ error }}</p>
        </div>

        <form @submit.prevent="verify" class="space-y-6">
          <div class="flex items-center justify-center gap-2 sm:gap-3">
            <input
              v-for="(_, i) in 6"
              :key="i"
              :value="otp[i]"
              @input="handleInput($event, i)"
              @keydown="onKeydown($event, i)"
              @paste="onPaste($event, i)"
              inputmode="numeric"
              autocomplete="one-time-code"
              pattern="[0-9]*"
              type="tel"
              :disabled="isLoading"
              class="w-11 h-14 sm:w-12 sm:h-14 text-center text-lg font-semibold rounded-xl border border-gray-300 bg-gray-50 text-gray-900 focus:outline-none focus:border-[#22282F] focus:ring-2 focus:ring-[#22282F]/15 transition-all disabled:opacity-50"
              :ref="el => setOtpRef(el, i)"
              maxlength="1"
            />
          </div>

          <button
            type="submit"
            :disabled="isLoading || !isComplete"
            class="w-full py-3 bg-[#22282F] text-white rounded-lg text-sm font-medium hover:bg-[#2a3139] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            <template v-if="isLoading">
              <div class="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              <span>Vérification…</span>
            </template>
            <span v-else>Vérifier</span>
          </button>
        </form>

        <button
          type="button"
          @click="backToLogin"
          class="mt-5 w-full text-sm text-gray-500 hover:text-[#22282F] transition-colors flex items-center justify-center gap-1"
        >
          <ArrowLeft class="w-4 h-4" />
          Revenir à la connexion
        </button>
      </div>

      <div class="mt-6 text-center">
        <p class="text-xs text-gray-400">v1.0 • © 2025 Ministère des Affaires Étrangères</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { ShieldCheck, AlertCircle, ArrowLeft } from 'lucide-vue-next'
import { useAuthStore } from '@/store/auth'

const store = useAuthStore()
const router = useRouter()

const otp = ref<string[]>(Array(6).fill(''))
const otpRefs = ref<Array<HTMLInputElement | null>>([])
const isLoading = ref(false)
const error = ref('')

const isComplete = computed(() => otp.value.every((d) => /^\d$/.test(d)))

// Si aucun pending_token (accès direct à /otp), retour au login
onMounted(() => {
  if (!store.pendingToken) {
    router.replace('/')
    return
  }
  focusIndex(0)
})

function setOtpRef(el: any, idx: number) {
  otpRefs.value[idx] = (el as HTMLInputElement) || null
}

function focusIndex(i: number) {
  const el = otpRefs.value[i]
  el?.focus()
  el?.select()
}

function distribute(digits: string) {
  const ds = digits.replace(/\D/g, '').slice(0, 6)
  for (let i = 0; i < 6; i++) otp.value[i] = ds[i] || ''
  const firstEmpty = otp.value.findIndex((d) => !d)
  focusIndex(firstEmpty === -1 ? 5 : firstEmpty)
}

function handleInput(e: Event, idx: number) {
  const target = e.target as HTMLInputElement
  const val = (target.value || '').replace(/\D/g, '')
  if (val.length > 1) {
    distribute(val)
    return
  }
  otp.value[idx] = val
  if (val && idx < 5) focusIndex(idx + 1)
}

function onKeydown(e: KeyboardEvent, idx: number) {
  const key = e.key
  if (key === 'Backspace') {
    if (otp.value[idx]) {
      otp.value[idx] = ''
    } else if (idx > 0) {
      otp.value[idx - 1] = ''
      focusIndex(idx - 1)
    }
    e.preventDefault()
  } else if (key === 'ArrowLeft' && idx > 0) {
    focusIndex(idx - 1)
    e.preventDefault()
  } else if (key === 'ArrowRight' && idx < 5) {
    focusIndex(idx + 1)
    e.preventDefault()
  }
}

function onPaste(e: ClipboardEvent) {
  const pasted = e.clipboardData?.getData('text') || ''
  const digits = pasted.replace(/\D/g, '')
  if (!digits) return
  e.preventDefault()
  distribute(digits)
}

function resetCode() {
  otp.value = Array(6).fill('')
  focusIndex(0)
}

async function verify() {
  const code = otp.value.join('')
  if (!/^\d{6}$/.test(code)) return

  error.value = ''
  isLoading.value = true
  try {
    const result = await store.verifyLogin(code)
    if (result.status === 'success') {
      router.push('/dashboard')
    } else if (result.status === 'expired') {
      error.value = result.error
      setTimeout(() => router.replace('/'), 1500)
    } else {
      error.value = (result as any).error || 'Code incorrect'
      resetCode()
    }
  } catch {
    error.value = 'Erreur de connexion au serveur'
  } finally {
    isLoading.value = false
  }
}

function backToLogin() {
  router.replace('/')
}
</script>
